import React, { useState } from 'react'
import axios from 'axios'

function ManualPropertyForm({ onPropertyCreated, onCancel }) {
  const [formData, setFormData] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    asking_price: '',
    estimated_value: '',
    monthly_rent: '',
    service_charge: '',
    ground_rent: '',
    property_type: 'flat',
    tenure: 'leasehold',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    year_built: '',
    epc_rating: '',
    has_garden: false,
    has_parking: false,
    nearest_station: '',
    station_distance_miles: '',
    description: ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.address_line_1.trim()) {
      newErrors.address_line_1 = 'Address is required'
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required'
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required'
    }
    if (!formData.asking_price || parseFloat(formData.asking_price) <= 0) {
      newErrors.asking_price = 'Valid asking price is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Convert string numbers to actual numbers
      const payload = {
        ...formData,
        asking_price: parseFloat(formData.asking_price) || 0,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        monthly_rent: formData.monthly_rent ? parseFloat(formData.monthly_rent) : null,
        service_charge: parseFloat(formData.service_charge) || 0,
        ground_rent: parseFloat(formData.ground_rent) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 1,
        sqft: formData.sqft ? parseFloat(formData.sqft) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        station_distance_miles: formData.station_distance_miles ? parseFloat(formData.station_distance_miles) : null
      }
      
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await axios.post(`${API_BASE}/api/v1/properties/manual`, payload)
      
      if (response.data.success) {
        onPropertyCreated(response.data.property)
      }
    } catch (error) {
      console.error('Failed to create property:', error)
      setErrors({
        submit: error.response?.data?.detail || 'Failed to create property. Please try again.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClasses = (fieldName) => `
    w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    ${errors[fieldName] ? 'border-red-500 bg-red-50' : 'border-gray-300'}
  `

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Enter Property Details</h3>
          <p className="text-gray-500 text-sm mt-1">
            Enter the property details manually for AI analysis
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {errors.submit && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Address Section */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 border-b pb-2">Address</h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
                className={inputClasses('address_line_1')}
                placeholder="e.g., 12 The Grove"
              />
              {errors.address_line_1 && (
                <p className="mt-1 text-sm text-red-600">{errors.address_line_1}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <input
                type="text"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
                className={inputClasses('address_line_2')}
                placeholder="e.g., Apartment 4B"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={inputClasses('city')}
                placeholder="e.g., London"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                className={inputClasses('postcode')}
                placeholder="e.g., E15 1EL"
              />
              {errors.postcode && (
                <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>
              )}
            </div>
          </div>
        </div>

        {/* Financial Section */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 border-b pb-2">Financial Details</h4>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asking Price (£) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="asking_price"
                value={formData.asking_price}
                onChange={handleChange}
                className={inputClasses('asking_price')}
                placeholder="e.g., 350000"
              />
              {errors.asking_price && (
                <p className="mt-1 text-sm text-red-600">{errors.asking_price}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Value (£)
              </label>
              <input
                type="number"
                name="estimated_value"
                value={formData.estimated_value}
                onChange={handleChange}
                className={inputClasses('estimated_value')}
                placeholder="e.g., 360000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Rent (£)
              </label>
              <input
                type="number"
                name="monthly_rent"
                value={formData.monthly_rent}
                onChange={handleChange}
                className={inputClasses('monthly_rent')}
                placeholder="e.g., 1975"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Charge (£/mo)
              </label>
              <input
                type="number"
                name="service_charge"
                value={formData.service_charge}
                onChange={handleChange}
                className={inputClasses('service_charge')}
                placeholder="e.g., 120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ground Rent (£/yr)
              </label>
              <input
                type="number"
                name="ground_rent"
                value={formData.ground_rent}
                onChange={handleChange}
                className={inputClasses('ground_rent')}
                placeholder="e.g., 250"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Council Tax Band
              </label>
              <select
                name="council_tax_band"
                value={formData.council_tax_band}
                onChange={handleChange}
                className={inputClasses('council_tax_band')}
              >
                <option value="">Select...</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
                <option value="F">F</option>
                <option value="G">G</option>
                <option value="H">H</option>
              </select>
            </div>
          </div>
        </div>

        {/* Property Features */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 border-b pb-2">Property Features</h4>
          
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Type
              </label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                className={inputClasses('property_type')}
              >
                <option value="flat">Flat</option>
                <option value="house">House</option>
                <option value="bungalow">Bungalow</option>
                <option value="commercial">Commercial</option>
                <option value="hmo">HMO</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenure
              </label>
              <select
                name="tenure"
                value={formData.tenure}
                onChange={handleChange}
                className={inputClasses('tenure')}
              >
                <option value="leasehold">Leasehold</option>
                <option value="freehold">Freehold</option>
                <option value="share_of_freehold">Share of Freehold</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bedrooms
              </label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleChange}
                className={inputClasses('bedrooms')}
                placeholder="e.g., 2"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bathrooms
              </label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleChange}
                className={inputClasses('bathrooms')}
                placeholder="e.g., 1"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size (sqft)
              </label>
              <input
                type="number"
                name="sqft"
                value={formData.sqft}
                onChange={handleChange}
                className={inputClasses('sqft')}
                placeholder="e.g., 650"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Built
              </label>
              <input
                type="number"
                name="year_built"
                value={formData.year_built}
                onChange={handleChange}
                className={inputClasses('year_built')}
                placeholder="e.g., 2010"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                EPC Rating
              </label>
              <select
                name="epc_rating"
                value={formData.epc_rating}
                onChange={handleChange}
                className={inputClasses('epc_rating')}
              >
                <option value="">Select...</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
                <option value="F">F</option>
                <option value="G">G</option>
              </select>
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="has_garden"
                checked={formData.has_garden}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-gray-700">Has Garden</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="has_parking"
                checked={formData.has_parking}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-gray-700">Has Parking</span>
            </label>
          </div>
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 border-b pb-2">Location</h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nearest Station
              </label>
              <input
                type="text"
                name="nearest_station"
                value={formData.nearest_station}
                onChange={handleChange}
                className={inputClasses('nearest_station')}
                placeholder="e.g., Stratford"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distance to Station (miles)
              </label>
              <input
                type="number"
                name="station_distance_miles"
                value={formData.station_distance_miles}
                onChange={handleChange}
                className={inputClasses('station_distance_miles')}
                placeholder="e.g., 0.2"
                step="0.1"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className={inputClasses('description')}
            placeholder="Brief description of the property..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating...
              </span>
            ) : (
              'Start Analysis'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ManualPropertyForm
